import React, { useState, useEffect } from 'react';
import inspectionStorage from '../services/inspectionStorage';
import syncManager from '../services/syncManager';
import pendingPhotosService from '../services/pendingPhotosService';
import ArmoireFormModal from './modals/ArmoireFormModal';
import CircuitsModal from './modals/CircuitsModal';
import ObservationModal from './modals/ObservationModal';
import QuickPhotoButton from './QuickPhotoButton';

/** Locaux pour datalist — aligné structure Etape10 PWA / web (zones_verifiees hiérarchique + legacy locaux). */
function getLocauxPlatsFromEtape10(etape10) {
  if (!etape10 || typeof etape10 !== 'object') return [];
  const out = [];
  if (Array.isArray(etape10.locaux)) {
    etape10.locaux.forEach((l) => {
      if (l?.nom) out.push({ nom: l.nom });
    });
  }
  const zones = etape10.zones_verifiees;
  if (!Array.isArray(zones)) return out;
  zones.forEach((node) => {
    if (node?.sous_categories && Array.isArray(node.sous_categories)) {
      node.sous_categories.forEach((bat) => {
        (bat?.locaux || []).forEach((local) => {
          if (local?.nom) out.push({ nom: local.nom });
        });
      });
    } else if (node?.nom && !node.sous_categories) {
      out.push({ nom: node.nom });
    }
  });
  return out;
}

export default function Etape9({ mission, missionId: propMissionId, onComplete, onBack }) {
  const missionId = mission?.id || mission || propMissionId;
  
  const [armoires, setArmoires] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [showCircuitsModal, setShowCircuitsModal] = useState(false);
  const [selectedArmoireIndex, setSelectedArmoireIndex] = useState(null);
  const [sourcesBT, setSourcesBT] = useState([]);
  const [locauxEtape10, setLocauxEtape10] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  
  const [showObsModal, setShowObsModal] = useState(false);
  const [obsContext, setObsContext] = useState(null);
  const [obsInitialPhotos, setObsInitialPhotos] = useState([]);
  const [obsPendingPhotoId, setObsPendingPhotoId] = useState(null);

  useEffect(() => {
    if (missionId) {
      loadData();
    }
  }, [missionId]);

  useEffect(() => {
    if (!missionId || !showAddModal) return;
    (async () => {
      try {
        const etape10 = await inspectionStorage.getStep(missionId, 'etape_10_localisations');
        setLocauxEtape10(getLocauxPlatsFromEtape10(etape10));
      } catch (e) {
        console.error('Erreur chargement locaux étape 10:', e);
      }
    })();
  }, [missionId, showAddModal]);

  const loadData = async () => {
    try {
      console.log('🔄 Chargement des données de l\'étape 9... missionId:', missionId);
      
      const savedData = await inspectionStorage.getStep(missionId, 'etape_9_armoires');
      if (savedData?.armoires) {
        setArmoires(savedData.armoires);
        console.log('✅ Armoires chargées:', savedData.armoires.length);
      }

      const etape8Data = await inspectionStorage.getStep(missionId, 'etape_8_sources_energie');
      if (etape8Data?.sources_bt) {
        setSourcesBT(etape8Data.sources_bt);
        console.log('✅ Sources BT chargées:', etape8Data.sources_bt.length);
      }
    } catch (error) {
      console.error('❌ Erreur chargement données:', error);
    }
  };

  const saveData = async (armoiresData) => {
    setIsSaving(true);
    try {
      const stepData = {
        armoires: armoiresData,
        timestamp: new Date().toISOString()
      };
      
      // 1. Sauvegarder en local (IndexedDB)
      await inspectionStorage.saveStep(missionId, 'etape_9_armoires', stepData);
      
      // 2. 🔥 AUTO-SYNC vers Base44 (si en ligne)
      syncManager.syncStep(missionId, 'etape_9_armoires', stepData);
      
      console.log('✅ Armoires sauvegardées');
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddArmoire = (armoireData) => {
    const newArmoires = [...armoires, { ...armoireData, circuits: [], obs_ids: [] }];
    setArmoires(newArmoires);
    saveData(newArmoires);
    setShowAddModal(false);
  };

  const handleEditArmoire = (armoireData) => {
    const newArmoires = [...armoires];
    newArmoires[editingIndex] = { ...armoires[editingIndex], ...armoireData };
    setArmoires(newArmoires);
    saveData(newArmoires);
    setShowAddModal(false);
    setEditingIndex(null);
  };

  const handleDeleteArmoire = (index) => {
    if (window.confirm('Supprimer cette armoire ?')) {
      const newArmoires = armoires.filter((_, i) => i !== index);
      setArmoires(newArmoires);
      saveData(newArmoires);
    }
  };

  const handleOpenCircuits = (index) => {
    setSelectedArmoireIndex(index);
    setShowCircuitsModal(true);
  };

  const handleUpdateCircuits = (circuits) => {
    const newArmoires = [...armoires];
    newArmoires[selectedArmoireIndex].circuits = circuits;
    setArmoires(newArmoires);
    saveData(newArmoires);
  };

  const buildArmoireContext = (armoire, index) => ({
    step_origin: 'etape_9_armoires',
    armoire_index: index,
    armoire_designation: armoire?.designation || '',
    localisation: armoire?.localisation || 'NON DÉFINI',
    element: `Armoire ${armoire?.designation || ''}`.trim(),
    element_ref: {
      etape: '9',
      type: 'armoire',
      path: { armoire_index: index },
    },
  });

  const buildCircuitContext = (circuit, circuitIndex, armoireIndex) => {
    const armoire = armoires[armoireIndex];
    return {
      step_origin: 'etape_9_circuits',
      armoire_index: armoireIndex,
      armoire_designation: armoire?.designation || '',
      circuit_index: circuitIndex,
      circuit_designation: circuit?.designation || '',
      localisation: armoire?.localisation || 'NON DÉFINI',
      element: `Circuit ${circuit?.designation || ''} (${armoire?.designation || 'Armoire'})`,
      circuit: circuit?.numero || '',
      element_ref: {
        etape: '9',
        type: 'circuit',
        path: {
          armoire_index: armoireIndex,
          circuit_index: circuitIndex,
        },
      },
    };
  };

  const openObsModalWith = (context, initialPhotos = [], pendingPhotoId = null) => {
    setObsContext(context);
    setObsInitialPhotos(initialPhotos);
    setObsPendingPhotoId(pendingPhotoId);
    setShowObsModal(true);
  };

  const handleOpenObservationCircuit = (circuit, circuitIndex) => {
    const armoireIndex = selectedArmoireIndex;
    openObsModalWith(buildCircuitContext(circuit, circuitIndex, armoireIndex));
  };

  const handleOpenObservation = (armoire, index) => {
    console.log('🔺 Bouton observation cliqué !');
    openObsModalWith(buildArmoireContext(armoire, index));
  };

  /**
   * Quick-photo → "Saisir maintenant" : on ouvre l'ObservationModal avec la photo
   * déjà rattachée. Le pendingPhotoId est mémorisé pour suppression au save.
   */
  const handleQuickPhotoOpenObs = (photo, context) => {
    if (!photo) return;
    const url = photo.url || photo.blob_base64;
    openObsModalWith(context, url ? [url] : [], photo.id);
  };

  const handleCloseObsModal = () => {
    setShowObsModal(false);
    setObsContext(null);
    setObsInitialPhotos([]);
    setObsPendingPhotoId(null);
  };

  const handleSaveObservation = async (observationData) => {
    try {
      const etape11Data = await inspectionStorage.getStep(missionId, 'etape_11_observations') || {};
      const currentObservations = etape11Data.observations || [];
      
      const dernierNumero = currentObservations.length > 0
        ? Math.max(...currentObservations.map(o => o.numero || 0))
        : 0;
      const nouveauNumero = dernierNumero + 1;
      
      const observation = {
        ...observationData,
        numero: nouveauNumero,
        created_date: observationData.created_date || new Date().toISOString(),
        photos: observationData.photos || []
      };
      
      const updatedObservations = [...currentObservations, observation];
      
      const obsStepData = {
        observations: updatedObservations,
        dernier_numero: nouveauNumero,
        timestamp: new Date().toISOString()
      };
      
      await inspectionStorage.saveStep(missionId, 'etape_11_observations', obsStepData);
      
      // 🔥 AUTO-SYNC observations vers Base44
      syncManager.syncStep(missionId, 'etape_11_observations', obsStepData);

      if (obsContext?.element_ref?.type === 'armoire') {
        const armoireIndex = obsContext.element_ref.path.armoire_index;
        const newArmoires = [...armoires];
        if (!newArmoires[armoireIndex].obs_ids) {
          newArmoires[armoireIndex].obs_ids = [];
        }
        newArmoires[armoireIndex].obs_ids.push(nouveauNumero);
        setArmoires(newArmoires);
        await saveData(newArmoires);
      } else if (obsContext?.element_ref?.type === 'circuit') {
        const { armoire_index, circuit_index } = obsContext.element_ref.path;
        const newArmoires = [...armoires];
        
        if (newArmoires[armoire_index] && newArmoires[armoire_index].circuits[circuit_index]) {
          if (!newArmoires[armoire_index].circuits[circuit_index].obs_ids) {
            newArmoires[armoire_index].circuits[circuit_index].obs_ids = [];
          }
          newArmoires[armoire_index].circuits[circuit_index].obs_ids.push(nouveauNumero);
          setArmoires(newArmoires);
          await saveData(newArmoires);
        }
      }

      console.log('✅ Observation sauvegardée:', nouveauNumero);

      // Si la création vient d'une pending photo, on la retire de la file d'attente
      if (obsPendingPhotoId) {
        try {
          await pendingPhotosService.remove(missionId, obsPendingPhotoId);
        } catch (e) {
          console.warn('[Etape9] suppression pending photo échouée :', e?.message || e);
        }
      }

      alert(`✅ Observation N°${nouveauNumero} créée avec succès !`);
    } catch (error) {
      console.error('❌ Erreur sauvegarde observation:', error);
      alert('Erreur lors de la sauvegarde de l\'observation');
    }

    handleCloseObsModal();
  };

  const handleComplete = async () => {
    if (armoires.length === 0) {
      alert('Veuillez ajouter au moins une armoire');
      return;
    }
    await saveData(armoires);
    onComplete();
  };

  // Libellé d'une source BT (étape 8). Aligné sur les phrases longues stockées
  // par l'étape 8 actuelle, avec fallback rétrocompatible vers les anciens slugs.
  const getSourceBTLabel = (source) => {
    if (!source) return 'Source BT';
    const nature = source.nature_designation || '';
    const lower = nature.toLowerCase();
    if (lower.includes('réseau de distribution public')) return 'Disjoncteur de Branchement';
    if (lower.includes('réseau interne')) return nature;
    if (lower.includes('groupe électrogène')) return 'Groupe électrogène';
    if (nature === 'reseau_public_bt') return 'Disjoncteur de Branchement';
    if (nature === 'transformateur_ht_bt') return 'Transformateur HT/BT';
    if (nature === 'groupe_electrogene') return 'Groupe électrogène';
    return nature || source.nature || source.localisation || 'Source BT';
  };

  const getArmoireAmontLabel = (armoireAmont) => {
    if (!armoireAmont) return '-';

    if (armoireAmont.startsWith('source_')) {
      const sourceIndex = parseInt(armoireAmont.replace('source_', ''), 10);
      return getSourceBTLabel(sourcesBT[sourceIndex]);
    }

    if (armoireAmont.startsWith('armoire_')) {
      const suffix = armoireAmont.replace('armoire_', '');
      const idx = parseInt(suffix, 10);
      // Format aligné app principale : armoire_<index>
      if (!Number.isNaN(idx) && idx >= 0 && idx < armoires.length) {
        return armoires[idx]?.designation || `Armoire ${idx + 1}`;
      }
      // Rétrocompatibilité : ancien format armoire_<designation>
      return suffix || '-';
    }

    return armoireAmont;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>⚡ Étape 9</h2>
        <h3 style={styles.subtitle}>Armoires électriques</h3>
      </div>

      <button
        onClick={() => {
          setEditingIndex(null);
          setShowAddModal(true);
        }}
        style={styles.addButton}
      >
        <span style={styles.addButtonIcon}>+</span>
        <span>Ajouter une armoire</span>
      </button>

      <div style={styles.armoiresList}>
        {armoires.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📦</div>
            <p style={styles.emptyText}>Aucune armoire créée</p>
            <p style={styles.emptySubtext}>Ajoutez votre première armoire</p>
          </div>
        ) : (
          armoires.map((armoire, index) => (
            <div key={index} style={styles.armoireCard}>
              <div style={styles.armoireHeader}>
                <div>
                  <div style={styles.armoireName}>{armoire.designation}</div>
                  <div style={styles.armoireLocation}>📍 {armoire.localisation}</div>
                </div>
                <div style={styles.circuitsBadge}>
                  {(armoire.circuits || []).length} circuits
                </div>
              </div>

              <div style={styles.armoireInfo}>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Code syn:</span>
                  <span style={styles.infoValue}>{armoire.code_syn || '-'}</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>Armoire amont:</span>
                  <span style={styles.infoValue}>
                    {getArmoireAmontLabel(armoire.armoire_amont)}
                  </span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>RC:</span>
                  <span style={styles.infoValue}>{armoire.rc || '-'} Ω</span>
                </div>
                <div style={styles.infoRow}>
                  <span style={styles.infoLabel}>IK3:</span>
                  <span style={styles.infoValue}>{armoire.ik3 ? `${armoire.ik3} kA` : '-'}</span>
                </div>
              </div>

              <div style={styles.armoireActions}>
                <button
                  onClick={() => handleOpenCircuits(index)}
                  style={styles.circuitsButton}
                >
                  ⚙️ Gérer circuits
                </button>
                <button
                  onClick={() => handleOpenObservation(armoire, index)}
                  style={styles.observationButton}
                  title="Ajouter une observation sur cette armoire"
                >
                  🔺
                </button>
                <QuickPhotoButton
                  missionId={missionId}
                  context={buildArmoireContext(armoire, index)}
                  onOpenObservation={handleQuickPhotoOpenObs}
                  title="Prendre une photo de l'armoire (observation ensuite)"
                />
                <button
                  onClick={() => {
                    setEditingIndex(index);
                    setShowAddModal(true);
                  }}
                  style={styles.editButton}
                >
                  ✏️
                </button>
                <button
                  onClick={() => handleDeleteArmoire(index)}
                  style={styles.deleteButton}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      <div style={styles.navigation}>
        <button onClick={onBack} style={styles.backButton}>
          ← Retour
        </button>
        <button
          onClick={handleComplete}
          style={styles.nextButton}
          disabled={isSaving || armoires.length === 0}
        >
          {isSaving ? 'Sauvegarde...' : 'Suivant →'}
        </button>
      </div>

      <ArmoireFormModal
        isOpen={showAddModal}
        armoire={editingIndex !== null ? armoires[editingIndex] : null}
        armoireIndex={editingIndex}
        sourcesBT={sourcesBT}
        armoires={armoires}
        locauxEtape10={locauxEtape10}
        onSave={editingIndex !== null ? handleEditArmoire : handleAddArmoire}
        onClose={() => {
          setShowAddModal(false);
          setEditingIndex(null);
        }}
      />

      {showCircuitsModal && selectedArmoireIndex !== null && armoires[selectedArmoireIndex] && (
        <CircuitsModal
          armoire={armoires[selectedArmoireIndex]}
          armoireIndex={selectedArmoireIndex}
          missionId={missionId}
          circuits={armoires[selectedArmoireIndex]?.circuits || []}
          onUpdateCircuits={handleUpdateCircuits}
          onClose={() => {
            setShowCircuitsModal(false);
            setSelectedArmoireIndex(null);
          }}
          onObservationCircuit={handleOpenObservationCircuit}
          onQuickPhotoOpenObs={handleQuickPhotoOpenObs}
        />
      )}

      {showObsModal && (
        <ObservationModal
          isOpen={showObsModal}
          onClose={handleCloseObsModal}
          onSave={handleSaveObservation}
          context={obsContext}
          initialPhotos={obsInitialPhotos}
          missionId={missionId}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px',
    maxWidth: '800px',
    margin: '0 auto',
    paddingBottom: '100px'
  },
  header: {
    marginBottom: '24px',
    textAlign: 'center'
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1e40af',
    marginBottom: '8px'
  },
  subtitle: {
    fontSize: '18px',
    color: '#64748b',
    fontWeight: '500'
  },
  addButton: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '20px',
    cursor: 'pointer',
    boxShadow: '0 4px 6px rgba(59, 130, 246, 0.3)'
  },
  addButtonIcon: {
    fontSize: '24px'
  },
  armoiresList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '24px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 20px',
    backgroundColor: '#f8fafc',
    borderRadius: '16px',
    border: '2px dashed #cbd5e1'
  },
  emptyIcon: {
    fontSize: '64px',
    marginBottom: '16px'
  },
  emptyText: {
    fontSize: '18px',
    color: '#475569',
    fontWeight: '600',
    marginBottom: '8px'
  },
  emptySubtext: {
    fontSize: '14px',
    color: '#94a3b8'
  },
  armoireCard: {
    backgroundColor: 'white',
    borderRadius: '16px',
    padding: '20px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    border: '1px solid #e2e8f0'
  },
  armoireHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '16px',
    paddingBottom: '12px',
    borderBottom: '2px solid #f1f5f9'
  },
  armoireName: {
    fontSize: '18px',
    fontWeight: 'bold',
    color: '#1e293b',
    marginBottom: '4px'
  },
  armoireLocation: {
    fontSize: '14px',
    color: '#64748b'
  },
  circuitsBadge: {
    backgroundColor: '#dbeafe',
    color: '#1e40af',
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600'
  },
  armoireInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    marginBottom: '16px'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: '#f8fafc',
    borderRadius: '8px'
  },
  infoLabel: {
    fontSize: '14px',
    color: '#64748b',
    fontWeight: '500'
  },
  infoValue: {
    fontSize: '14px',
    color: '#1e293b',
    fontWeight: '600'
  },
  armoireActions: {
    display: 'flex',
    gap: '8px'
  },
  circuitsButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  observationButton: {
    width: '48px',
    height: '48px',
    backgroundColor: '#fee2e2',
    border: '2px solid #ef4444',
    borderRadius: '8px',
    fontSize: '18px',
    cursor: 'pointer'
  },
  editButton: {
    width: '48px',
    height: '48px',
    backgroundColor: '#f1f5f9',
    border: '2px solid #3b82f6',
    borderRadius: '8px',
    fontSize: '18px',
    cursor: 'pointer'
  },
  deleteButton: {
    width: '48px',
    height: '48px',
    backgroundColor: '#f1f5f9',
    border: '2px solid #ef4444',
    borderRadius: '8px',
    fontSize: '18px',
    cursor: 'pointer'
  },
  navigation: {
    display: 'flex',
    gap: '12px',
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    right: '20px',
    maxWidth: '800px',
    margin: '0 auto',
    backgroundColor: 'white',
    padding: '16px',
    borderRadius: '16px',
    boxShadow: '0 -4px 12px rgba(0, 0, 0, 0.1)'
  },
  backButton: {
    flex: 1,
    padding: '16px',
    backgroundColor: 'white',
    color: '#64748b',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer'
  },
  nextButton: {
    flex: 2,
    padding: '16px',
    backgroundColor: '#8b5cf6',
    color: 'white',
    border: 'none',
    borderRadius: '12px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
  }
};


